import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Calendar, Mail, MapPin, CheckCircle2, Loader2, Send } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under $2,000", "$2,000 – $5,000", "$5,000 – $10,000", "$10,000+"];
const TIME_SLOTS = ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

const inputCls =
  "h-13 w-full rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-base text-slate-900 outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-kinetic";

const SOCIALS = [
  { name: "Instagram", href: "https://www.instagram.com/covellicanada/" },
  { name: "TikTok", href: "https://www.tiktok.com/@covellica" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", website: "", budget: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");
  const [booking, setBooking] = useState({ name: "", email: "", date: "", time: "", notes: "" });
  const [bState, setBState] = useState("idle");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setB = (k) => (e) => setBooking({ ...booking, [k]: e.target.value });

  const submitBooking = async (e) => {
    e.preventDefault();
    if (
      !booking.name.trim() ||
      !booking.date ||
      !booking.time ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(booking.email)
    ) {
      setBState("error");
      return;
    }
    setBState("loading");
    try {
      await axios.post(`${API}/appointments`, booking);
      setBState("success");
    } catch {
      setBState("error");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.message.trim() || !form.budget) {
      setState("error");
      setError("Please fill in name, budget and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setState("error");
      setError("Please enter a valid email address.");
      return;
    }
    setState("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setState("success");
    } catch {
      setState("error");
      setError("Something went wrong. Please try again or email us directly.");
    }
  };

  return (
    <>
      <Seo
        title="Book a Call — Covelli"
        siteName="Covelli"
        description="Book a strategy call or send Covelli a message. No pressure, just clarity."
      />
      <section data-testid="contact-hero" className="mx-auto max-w-7xl px-6 pb-6 pt-32 md:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-kinetic"
        >
          Contact
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl md:text-6xl"
        >
          Let's Talk Growth
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600"
        >
          Book a strategy call or send us a message. No pressure, just clarity.
        </motion.p>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-28 pt-8">
        <div className="grid gap-10 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-6 lg:col-span-2"
          >
            <div className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <AnimatePresence mode="wait">
                {bState === "success" ? (
                  <motion.div
                    key="booking-success"
                    data-testid="booking-success-message"
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="flex min-h-56 flex-col items-center justify-center gap-4 text-center"
                  >
                    <CheckCircle2 size={32} className="text-skybrand" />
                    <p className="font-display text-lg font-bold text-slate-900">Request received.</p>
                    <p className="max-w-xs text-sm leading-relaxed text-slate-600">
                      We'll confirm your strategy call by email shortly.
                    </p>
                  </motion.div>
                ) : (
                  <motion.form key="booking-form" data-testid="booking-form" onSubmit={submitBooking} exit={{ opacity: 0, y: -10 }} className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Calendar size={20} className="text-skybrand" />
                      <h2 className="font-display text-lg font-bold text-slate-900">Book a strategy call</h2>
                    </div>
                    <input data-testid="booking-name-input" value={booking.name} onChange={setB("name")} placeholder="Name" className={inputCls} />
                    <input data-testid="booking-email-input" type="email" value={booking.email} onChange={setB("email")} placeholder="Email" className={inputCls} />
                    <div className="grid gap-4 sm:grid-cols-2">
                      <input
                        data-testid="booking-date-input"
                        type="date"
                        value={booking.date}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={setB("date")}
                        className={`${inputCls} ${booking.date ? "text-slate-900" : "text-slate-400"}`}
                      />
                      <select
                        data-testid="booking-time-select"
                        value={booking.time}
                        onChange={setB("time")}
                        className={`${inputCls} ${booking.time ? "text-slate-900" : "text-slate-400"}`}
                      >
                        <option value="" disabled>Pick a time</option>
                        {TIME_SLOTS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                    <textarea
                      data-testid="booking-notes-input"
                      value={booking.notes}
                      onChange={setB("notes")}
                      placeholder="Anything we should know? (optional)"
                      rows={2}
                      className="w-full resize-none rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-base text-slate-900 outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-kinetic"
                    />
                    {bState === "error" && (
                      <p data-testid="booking-error-message" className="font-mono text-xs uppercase tracking-widest text-kinetic">
                        Please complete name, email, date and time.
                      </p>
                    )}
                    <button
                      data-testid="booking-submit-button"
                      type="submit"
                      disabled={bState === "loading"}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-kinetic px-7 py-3.5 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover disabled:opacity-60"
                    >
                      {bState === "loading" ? <Loader2 size={15} className="animate-spin" /> : null}
                      Request This Time
                    </button>
                    <p className="text-center font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      All times Toronto (ET) · Confirmed by email
                    </p>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <h2 className="font-mono text-[11px] uppercase tracking-[0.3em] text-skybrand">
                Reach us directly
              </h2>
              <a
                data-testid="contact-email-link"
                href="mailto:team@covelli.ca"
                className="mt-5 flex items-center gap-3 text-base font-medium text-slate-900 transition-colors duration-300 hover:text-kinetic"
              >
                <Mail size={17} className="text-kinetic" /> team@covelli.ca
              </a>
              <p className="mt-4 flex items-center gap-3 text-base text-slate-600">
                <MapPin size={17} className="text-kinetic" /> Toronto, Ontario
              </p>
              <div className="mt-6 flex gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.name}
                    data-testid={`contact-social-${s.name.toLowerCase()}`}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-full border border-slate-200 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-slate-500 transition-colors duration-300 hover:border-kinetic hover:text-kinetic"
                  >
                    {s.name}
                  </a>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:p-10 lg:col-span-3"
          >
            <AnimatePresence mode="wait">
              {state === "success" ? (
                <motion.div
                  key="success"
                  data-testid="contact-success-message"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="flex h-full min-h-72 flex-col items-center justify-center gap-4 text-center"
                >
                  <CheckCircle2 size={36} className="text-skybrand" />
                  <p className="font-display text-xl font-bold text-slate-900">Message sent.</p>
                  <p className="max-w-sm text-sm leading-relaxed text-slate-600">
                    Thanks for reaching out — we'll get back to you within one
                    business day with honest answers.
                  </p>
                </motion.div>
              ) : (
                <motion.form key="form" data-testid="contact-form" onSubmit={submit} exit={{ opacity: 0, y: -10 }} className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <input data-testid="contact-name-input" value={form.name} onChange={set("name")} placeholder="Name" className={inputCls} />
                    <input data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={inputCls} />
                  </div>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <input data-testid="contact-company-input" value={form.company} onChange={set("company")} placeholder="Company / brand" className={inputCls} />
                    <input data-testid="contact-website-input" value={form.website} onChange={set("website")} placeholder="Website (optional)" className={inputCls} />
                  </div>
                  <select
                    data-testid="contact-budget-select"
                    value={form.budget}
                    onChange={set("budget")}
                    className={`${inputCls} ${form.budget ? "text-slate-900" : "text-slate-400"}`}
                  >
                    <option value="" disabled>Monthly marketing budget (CAD)</option>
                    {BUDGETS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                  <textarea
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Tell us where you are and where you want to go..."
                    rows={5}
                    className="w-full resize-none rounded-xl border border-slate-200 bg-white px-5 py-3.5 text-base text-slate-900 outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-kinetic"
                  />
                  {state === "error" && error && (
                    <p data-testid="contact-error-message" className="font-mono text-xs uppercase tracking-widest text-kinetic">
                      {error}
                    </p>
                  )}
                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={state === "loading"}
                    className="group inline-flex items-center gap-2 rounded-full bg-kinetic px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-kinetic-hover disabled:opacity-60"
                  >
                    {state === "loading" ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                    Send Message
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>
    </>
  );
}
